import React from "react";
import { useLocation, Link } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { Helmet } from "react-helmet";
import { useAuth } from "../contexts/AuthContext";
import {
  MEALS_ONLY_PRODUCT,
  isPathAllowedForUser,
  isProductPreviewUser,
} from "../utils/productSurface";
import { MealsPageHeader, mealsPageFrameSx } from "../Components/MealsPageChrome";
import { tvNavyTextMuted } from "../utils/authBranding";

/**
 * Paths that are hidden from nav and not ready for production.
 * In production, visiting these URLs shows "Coming soon" instead of the page.
 * When MEALS_ONLY_PRODUCT is on, anything outside the meals allowlist is blocked
 * in all environments so new users never land on unfinished features — except
 * owner/beta product-preview users who can open extra product routes.
 */
export const DISABLED_FOR_PROD_PATHS = [
  "/household",
  "/lists",
  "/fitness",
  "/docs",
  "/step",
  "/schedule",
];

const ComingSoonGuard = ({ children, path }) => {
  const location = useLocation();
  const { user } = useAuth();
  const pathname = path || location.pathname;
  const isProduction = process.env.NODE_ENV === "production";
  const previewUser = isProductPreviewUser(user?.email);

  const blockedByMealsOnly =
    MEALS_ONLY_PRODUCT && !isPathAllowedForUser(pathname, user?.email);
  const blockedByLegacyList =
    isProduction &&
    DISABLED_FOR_PROD_PATHS.includes(pathname) &&
    !previewUser;

  if (blockedByMealsOnly || blockedByLegacyList) {
    return (
      <Box sx={mealsPageFrameSx}>
        <Helmet>
          <title>Coming soon</title>
        </Helmet>
        <MealsPageHeader title="Coming soon" backTo="/" />
        <Box
          sx={{
            borderRadius: 2,
            border: "1px solid rgba(255,255,255,0.1)",
            bgcolor: "rgba(255,255,255,0.03)",
            p: { xs: 2.5, sm: 3 },
            textAlign: "center",
          }}
        >
          <Typography variant="body2" sx={{ color: tvNavyTextMuted, mb: 2, lineHeight: 1.55 }}>
            {MEALS_ONLY_PRODUCT
              ? "This part of LifeStack isn’t in the meals launch yet. Head home to plan meals and build your list."
              : "This section isn’t ready yet. We’re working on it!"}
          </Typography>
          <Button
            component={Link}
            to="/"
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: "#94a3b8",
              color: "#041018",
              "&:hover": { bgcolor: "#94a3b8", filter: "brightness(1.06)" },
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Box>
    );
  }

  return children;
};

export default ComingSoonGuard;
